import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTelegramPlane } from "@fortawesome/free-brands-svg-icons";
import { useCreateTelegramLinkMutation } from "../../../services/rtkApi/messengerReachApi";
import type { TelegramReachState } from "../api/types";

const TELEGRAM_BOT_URL = "https://t.me/n450s_bot";
// link tokens live 10 minutes on the messenger; refresh a little before that
const LINK_REFRESH_MS = 8 * 60 * 1000;

interface Props {
  state: TelegramReachState | undefined;
  /** Called when the person taps the link, so the step polls faster. */
  onOpened: () => void;
}

/**
 * Option B in the reach step: one-tap Telegram link. The same behaviour as
 * ConnectTelegramButton in profile_page/TelegramSettings.tsx (not exported):
 * a t.me deep link with a one-time token, so tapping Start links the account.
 * The token is minted only once reach has loaded and says Telegram is not
 * linked; until then, and for a linked but blocked bot, the plain bot link.
 */
const TelegramOption: React.FC<Props> = ({ state, onOpened }) => {
  const [createLink] = useCreateTelegramLinkMutation();
  const [url, setUrl] = useState<string | null>(null);
  const linked = state === "ok";
  const needsLink = state === "none";

  useEffect(() => {
    if (!needsLink) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await createLink().unwrap();
        if (!cancelled && res.success && res.data?.url) setUrl(res.data.url);
      } catch {
        // keep the plain bot link
      }
    };
    void load();
    const timer = setInterval(load, LINK_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [createLink, needsLink]);

  let body: React.ReactNode;
  if (state === "ok") {
    body = <p>Telegram is linked. The game will message you there.</p>;
  } else if (state === "muted") {
    body = <p>Telegram is linked but muted. Unmute the bot, then tap Check again.</p>;
  } else {
    body = (
      <>
        <p>
          {state === "blocked"
            ? "The n450s bot is blocked in your Telegram. Open it, tap Restart or Start, then tap Check again."
            : "Get the game's messages from the n450s bot in Telegram."}
        </p>
        <a
          href={(needsLink && url) || TELEGRAM_BOT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="gm-button gm-button--sky gm-button--block"
          onClick={onOpened}
        >
          <FontAwesomeIcon icon={faTelegramPlane} /> {state === "blocked" ? "Open Telegram" : "Link Telegram"}
        </a>
        <p className="gm-option__note">Opens Telegram. Tap Start there and you&apos;re linked.</p>
      </>
    );
  }

  return (
    <section className="gm-option" data-on={linked} aria-label="Telegram">
      <h3 className="gm-option__head">
        <FontAwesomeIcon icon={faTelegramPlane} /> Telegram
        {linked ? <span className="gm-option__on">On</span> : null}
      </h3>
      {body}
    </section>
  );
};

export default TelegramOption;
